import React, { useState, useEffect, useCallback } from 'react';
import {
  Briefcase, TrendingUp, TrendingDown, DollarSign, PieChart, Activity,
  Plus, Trash2, Eye, EyeOff, BarChart3, Target, RefreshCw, AlertTriangle,
  Loader2, XCircle, ArrowUpRight, ArrowDownRight, Shield
} from 'lucide-react';
import brokerAPI from '../../api/broker';

const PortfolioTracker = () => {
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [portfolioHistory, setPortfolioHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showValues, setShowValues] = useState(true);
  const [filter, setFilter] = useState('all');
  const [closingSymbol, setClosingSymbol] = useState(null);
  const [connected, setConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const [acctRes, posRes] = await Promise.all([
        brokerAPI.getAccount(),
        brokerAPI.getPositions(),
      ]);
      setAccount(acctRes);
      setPositions(posRes.positions || []);
      setConnected(true);
      setLastUpdate(new Date());

      // Fetch history (non-blocking)
      brokerAPI.getPortfolioHistory('1M', '1D')
        .then(h => setPortfolioHistory(h))
        .catch(() => {});
    } catch (err) {
      setError(err.message);
      setConnected(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000); // refresh every 15s
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleClosePosition = async (symbol) => {
    if (!confirm(`Close entire ${symbol} position?`)) return;
    setClosingSymbol(symbol);
    try {
      await brokerAPI.closePosition(symbol);
      await fetchData();
    } catch (err) {
      alert(`Failed to close ${symbol}: ${err.message}`);
    } finally {
      setClosingSymbol(null);
    }
  };

  const calculateStats = () => {
    const filtered = filter === 'all'
      ? positions
      : positions.filter(p => {
          if (filter === 'stock') return p.asset_class === 'us_equity';
          if (filter === 'crypto') return p.asset_class === 'crypto';
          return true;
        });

    const totalValue = filtered.reduce((s, p) => s + parseFloat(p.market_value || 0), 0);
    const totalCost = filtered.reduce((s, p) => s + parseFloat(p.cost_basis || 0), 0);
    const totalPL = filtered.reduce((s, p) => s + parseFloat(p.unrealized_pl || 0), 0);
    const totalDayPL = filtered.reduce((s, p) => s + parseFloat(p.unrealized_intraday_pl || 0), 0);

    return { totalValue, totalCost, totalPL, totalDayPL, count: filtered.length };
  };

  const stats = calculateStats();
  const equity = account ? parseFloat(account.equity || 0) : 0;
  const cash = account ? parseFloat(account.cash || 0) : 0;
  const buyingPower = account ? parseFloat(account.buying_power || 0) : 0;

  const fmt = (v) => {
    if (!showValues) return '••••••';
    return `$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const fmtPct = (v) => {
    const n = parseFloat(v) * 100;
    return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
  };

  // Mini SVG sparkline from portfolio history
  const renderSparkline = () => {
    if (!portfolioHistory?.equity?.length) return null;
    const data = portfolioHistory.equity;
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const w = 200, h = 40;
    const points = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * h}`).join(' ');
    const color = data[data.length - 1] >= data[0] ? '#10b981' : '#ef4444';

    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block' }}>
        <polyline points={points} fill="none" stroke={color} strokeWidth="2" />
      </svg>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', background: 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)', minHeight: 'calc(100vh - 64px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <Loader2 size={48} color="#3b82f6" style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ color: '#94a3b8', marginTop: '16px' }}>Connecting to Alpaca...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', background: 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)', minHeight: 'calc(100vh - 64px)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <h1 style={{ margin: 0, color: 'white', fontSize: '32px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Briefcase size={36} color="#10b981" />
            Portfolio
            {account?.paper && (
              <span style={{ fontSize: '12px', padding: '4px 10px', background: '#f59e0b22', color: '#f59e0b', borderRadius: '6px', border: '1px solid #f59e0b44' }}>
                PAPER
              </span>
            )}
          </h1>
          <p style={{ margin: '8px 0 0 0', color: '#94a3b8', fontSize: '14px' }}>
            <span style={{ color: connected ? '#10b981' : '#ef4444', marginRight: '6px' }}>●</span>
            {connected ? 'Connected to Alpaca' : 'Disconnected'} • {positions.length} positions
            {lastUpdate && ` • Updated ${lastUpdate.toLocaleTimeString()}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button onClick={() => setShowValues(!showValues)} style={{ padding: '10px', background: '#1e293b', color: 'white', border: '1px solid #334155', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }} title={showValues ? 'Hide values' : 'Show values'}>
            {showValues ? <Eye size={18} /> : <EyeOff size={18} />}
          </button>
          <button onClick={fetchData} style={{ padding: '10px', background: '#1e293b', color: '#3b82f6', border: '1px solid #334155', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }} title="Refresh">
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '16px', background: '#7f1d1d33', border: '1px solid #ef4444', borderRadius: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <AlertTriangle size={20} color="#ef4444" />
          <span style={{ color: '#fca5a5' }}>{error}</span>
        </div>
      )}

      {/* Account Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {/* Equity */}
        <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', padding: '20px', borderRadius: '12px', border: '1px solid #334155' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <DollarSign size={20} color="#10b981" />
            <span style={{ color: '#94a3b8', fontSize: '13px' }}>Equity</span>
          </div>
          <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>{fmt(equity)}</div>
          {renderSparkline()}
        </div>

        {/* Unrealized P&L */}
        <div style={{ background: `linear-gradient(135deg, ${stats.totalPL >= 0 ? '#065f46' : '#7f1d1d'} 0%, ${stats.totalPL >= 0 ? '#064e3b' : '#991b1b'} 100%)`, padding: '20px', borderRadius: '12px', border: `1px solid ${stats.totalPL >= 0 ? '#10b981' : '#ef4444'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            {stats.totalPL >= 0 ? <TrendingUp size={20} color="#10b981" /> : <TrendingDown size={20} color="#ef4444" />}
            <span style={{ color: '#e5e7eb', fontSize: '13px' }}>Unrealized P&L</span>
          </div>
          <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>{stats.totalPL >= 0 ? '+' : '-'}{fmt(stats.totalPL)}</div>
          <div style={{ color: '#e5e7eb', fontSize: '13px', marginTop: '4px' }}>Day: {stats.totalDayPL >= 0 ? '+' : '-'}{fmt(stats.totalDayPL)}</div>
        </div>

        {/* Cash */}
        <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', padding: '20px', borderRadius: '12px', border: '1px solid #334155' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Activity size={20} color="#3b82f6" />
            <span style={{ color: '#94a3b8', fontSize: '13px' }}>Cash</span>
          </div>
          <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>{fmt(cash)}</div>
        </div>

        {/* Buying Power */}
        <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', padding: '20px', borderRadius: '12px', border: '1px solid #334155' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Shield size={20} color="#f59e0b" />
            <span style={{ color: '#94a3b8', fontSize: '13px' }}>Buying Power</span>
          </div>
          <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>{fmt(buyingPower)}</div>
          {account?.daytrade_count >= 3 && (
            <div style={{ color: '#f59e0b', fontSize: '11px', marginTop: '4px' }}>PDT: {account.daytrade_count}/3 day trades</div>
          )}
        </div>
      </div>

      {/* Filter Tabs */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        {['all', 'stock', 'crypto'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '8px 18px', background: filter === f ? '#3b82f6' : '#1e293b',
            color: 'white', border: filter === f ? '1px solid #60a5fa' : '1px solid #334155',
            borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: filter === f ? 'bold' : 'normal', textTransform: 'capitalize'
          }}>
            {f === 'stock' ? 'Stocks' : f === 'crypto' ? 'Crypto' : 'All'}
          </button>
        ))}
      </div>

      {/* Positions Table */}
      <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '12px', border: '1px solid #334155', overflow: 'hidden' }}>
        {positions.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <Briefcase size={48} color="#334155" style={{ marginBottom: '16px' }} />
            <p style={{ color: '#94a3b8', fontSize: '16px' }}>No open positions</p>
            <p style={{ color: '#64748b', fontSize: '13px' }}>Place an order to see your positions here</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#0f172a', borderBottom: '1px solid #334155' }}>
                <th style={{ padding: '14px', textAlign: 'left', color: '#94a3b8', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase' }}>Symbol</th>
                <th style={{ padding: '14px', textAlign: 'right', color: '#94a3b8', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase' }}>Qty</th>
                <th style={{ padding: '14px', textAlign: 'right', color: '#94a3b8', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase' }}>Avg Entry</th>
                <th style={{ padding: '14px', textAlign: 'right', color: '#94a3b8', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase' }}>Current</th>
                <th style={{ padding: '14px', textAlign: 'right', color: '#94a3b8', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase' }}>Mkt Value</th>
                <th style={{ padding: '14px', textAlign: 'right', color: '#94a3b8', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase' }}>P&L</th>
                <th style={{ padding: '14px', textAlign: 'right', color: '#94a3b8', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase' }}>Today</th>
                <th style={{ padding: '14px', textAlign: 'center', color: '#94a3b8', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {positions
                .filter(p => {
                  if (filter === 'stock') return p.asset_class === 'us_equity';
                  if (filter === 'crypto') return p.asset_class === 'crypto';
                  return true;
                })
                .map(pos => {
                  const pl = parseFloat(pos.unrealized_pl || 0);
                  const plPct = parseFloat(pos.unrealized_plpc || 0);
                  const dayPl = parseFloat(pos.unrealized_intraday_pl || 0);
                  const dayPct = parseFloat(pos.unrealized_intraday_plpc || 0);
                  const isPositive = pl >= 0;
                  const isDayPositive = dayPl >= 0;

                  return (
                    <tr key={pos.symbol} style={{ borderBottom: '1px solid #334155', transition: 'background 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#1e293b'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '14px' }}>
                        <div style={{ color: 'white', fontSize: '15px', fontWeight: 'bold' }}>{pos.symbol}</div>
                        <div style={{ color: '#64748b', fontSize: '11px' }}>{pos.asset_class === 'us_equity' ? 'Stock' : 'Crypto'} • {pos.side}</div>
                      </td>
                      <td style={{ padding: '14px', textAlign: 'right', color: 'white', fontSize: '14px' }}>
                        {parseFloat(pos.qty).toLocaleString()}
                      </td>
                      <td style={{ padding: '14px', textAlign: 'right', color: 'white', fontSize: '14px' }}>
                        {fmt(parseFloat(pos.avg_entry_price || 0))}
                      </td>
                      <td style={{ padding: '14px', textAlign: 'right', color: 'white', fontSize: '14px' }}>
                        {fmt(parseFloat(pos.current_price || 0))}
                      </td>
                      <td style={{ padding: '14px', textAlign: 'right', color: 'white', fontSize: '15px', fontWeight: 'bold' }}>
                        {fmt(parseFloat(pos.market_value || 0))}
                      </td>
                      <td style={{ padding: '14px', textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', background: isPositive ? '#065f4633' : '#7f1d1d33', borderRadius: '6px', border: `1px solid ${isPositive ? '#10b981' : '#ef4444'}` }}>
                          {isPositive ? <ArrowUpRight size={14} color="#10b981" /> : <ArrowDownRight size={14} color="#ef4444" />}
                          <span style={{ color: isPositive ? '#10b981' : '#ef4444', fontSize: '13px', fontWeight: 'bold' }}>
                            {fmt(pl)} ({fmtPct(plPct)})
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '14px', textAlign: 'right' }}>
                        <span style={{ color: isDayPositive ? '#10b981' : '#ef4444', fontSize: '13px' }}>
                          {isDayPositive ? '+' : '-'}{fmt(dayPl)}
                        </span>
                      </td>
                      <td style={{ padding: '14px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleClosePosition(pos.symbol)}
                          disabled={closingSymbol === pos.symbol}
                          style={{
                            padding: '6px 14px', background: '#ef4444', color: 'white', border: 'none',
                            borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold',
                            opacity: closingSymbol === pos.symbol ? 0.5 : 1,
                          }}
                        >
                          {closingSymbol === pos.symbol ? 'Closing...' : 'Close'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        )}
      </div>

      {/* Portfolio Allocation Bar */}
      {positions.length > 0 && (
        <div style={{ marginTop: '24px', background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', padding: '20px', borderRadius: '12px', border: '1px solid #334155' }}>
          <h3 style={{ color: 'white', margin: '0 0 16px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <PieChart size={18} color="#3b82f6" /> Allocation
          </h3>
          <div style={{ display: 'flex', height: '24px', borderRadius: '6px', overflow: 'hidden', marginBottom: '12px' }}>
            {positions.map((p, i) => {
              const pct = (parseFloat(p.market_value || 0) / stats.totalValue) * 100;
              const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
              return (
                <div key={p.symbol} style={{ width: `${pct}%`, background: colors[i % colors.length], minWidth: pct > 0 ? '2px' : '0' }} title={`${p.symbol}: ${pct.toFixed(1)}%`} />
              );
            })}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {positions.map((p, i) => {
              const pct = (parseFloat(p.market_value || 0) / stats.totalValue) * 100;
              const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
              return (
                <div key={p.symbol} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: colors[i % colors.length] }} />
                  <span style={{ color: '#94a3b8', fontSize: '12px' }}>{p.symbol} {pct.toFixed(1)}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default PortfolioTracker;
